import { MINDMAP_FARBEN, type BildPunkt, type MindmapFarbe } from '@beholder/shared/module/mindmap';
import { FARB_NAMEN, FARBTON } from './symbole';

/** Weg zu einem Punkt im Baum: Indizes der Kinder ab der Wurzel ([] = Wurzel). */
type Weg = number[];

function aendern(p: BildPunkt, weg: Weg, fn: (x: BildPunkt) => BildPunkt | null): BildPunkt | null {
  if (!weg.length) return fn(p);
  const [i, ...rest] = weg;
  const kinder: BildPunkt[] = [];
  p.kinder.forEach((k, j) => {
    if (j !== i) kinder.push(k);
    else {
      const neu = aendern(k, rest, fn);
      if (neu) kinder.push(neu);
    }
  });
  return { ...p, kinder };
}

export function punkteZaehlen(p: BildPunkt): number {
  return (p.titel.trim() ? 1 : 0) + p.kinder.reduce((a, k) => a + punkteZaehlen(k), 0);
}

/** Erkannter Baum aus einem Bild – Titel, Farbe und Äste lassen sich vor dem Übernehmen ändern. */
export function BildBaumBearbeiten({ baum, aendern: setzen, gesperrt }: { baum: BildPunkt; aendern(b: BildPunkt): void; gesperrt?: boolean }) {
  const an = (weg: Weg, fn: (x: BildPunkt) => BildPunkt | null) => {
    const neu = aendern(baum, weg, fn);
    if (neu) setzen(neu);
  };

  const zeile = (p: BildPunkt, weg: Weg) => {
    const f = p.farbe ? FARBTON[p.farbe] : null;
    const wurzel = weg.length === 0;
    return (
      <li key={weg.join('.')}>
        <div className="mm-bild-zeile" style={f ? { borderLeftColor: f } : undefined}>
          <input
            className="eingabe"
            value={p.titel}
            disabled={gesperrt}
            // neu ergänzter (leerer) Unterpunkt bekommt gleich den Fokus
            autoFocus={!wurzel && p.titel === '' && p.kinder.length === 0}
            aria-label={wurzel ? 'Mitte der Zeichnung' : `Punkt auf Ebene ${weg.length}`}
            placeholder={wurzel ? 'Mitte der Zeichnung' : 'Titel (leer = wird weggelassen)'}
            onChange={(e) => an(weg, (x) => ({ ...x, titel: e.target.value }))}
            data-wurzel={wurzel ? '' : undefined}
          />
          <select
            className="eingabe mm-bild-farbe"
            value={p.farbe ?? ''}
            disabled={gesperrt}
            aria-label="Farbe"
            title="Farbe"
            onChange={(e) => an(weg, (x) => ({ ...x, farbe: (e.target.value || null) as MindmapFarbe | null }))}
          >
            <option value="">ohne Farbe</option>
            {MINDMAP_FARBEN.map((m) => (
              <option key={m} value={m}>
                {FARB_NAMEN[m]}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="knopf knopf-leise knopf-klein"
            disabled={gesperrt}
            title="Unterpunkt hinzufügen"
            aria-label={`Unterpunkt unter „${p.titel}“ hinzufügen`}
            onClick={() => an(weg, (x) => ({ ...x, kinder: [...x.kinder, { titel: '', farbe: null, kinder: [] }] }))}
          >
            +
          </button>
          {!wurzel && (
            <button
              type="button"
              className="knopf knopf-leise knopf-klein"
              disabled={gesperrt}
              title="Mit Unterpunkten weglassen"
              aria-label={`„${p.titel}“ weglassen`}
              onClick={() => an(weg, () => null)}
            >
              ×
            </button>
          )}
        </div>
        {p.beschreibung && <div className="leise klein mm-bild-beschreibung">{p.beschreibung}</div>}
        {p.kinder.length > 0 && <ul>{p.kinder.map((k, i) => zeile(k, [...weg, i]))}</ul>}
      </li>
    );
  };

  return <ul className="mm-bild-baum">{zeile(baum, [])}</ul>;
}
